import StringMethods from '~/utils/character'
import type { CustomerInformation, HospitalListSchema, PolicyResponse } from '~/types'

export type ApiMediaType = {
	owner_name: string
	address: string
	registration_number: string
	engine_number: string
	chassis_number: string
	vehicle_make: string
	vehicle_model: string
	colour: string
}
export interface HospitalVisitResponse {
	customer_id: string
	policy_id: string
	purchase_id: string
	customer: CustomerInformation
	masterHospital: string
	rating: string
	supportMessages: string[]
	policy: PolicyResponse
}

export function useResourcesApi() {
	const { toCapitalize } = StringMethods()
	const { cookie } = useAuth()
	async function getCoverPeriod() {
		const { data } = await useAPI<IRequestResponse<string[]>>('/resources/cover-periods')
		if (data.value?.data) {
			const { setCoverPeriod } = useProducts()
			const result = data.value.data.map(item => ({
				label: item,
				value: item
			}))
			if (result.length) {
				setCoverPeriod(result)
			}
		}
		return data
	}

	async function GetTitles() {
		const { data } = await useAPI<IRequestResponse<string[]>>('/travel/titles')
		if (data.value?.data) {
			return data.value.data.map(item => ({
				label: item,
				value: item
			}))
		}
		return []
	}
	async function GetAiicoTitles() {
		const { data } = await useAPI<IRequestResponse<string[]>>('/aiico-titles')
		if (data.value?.data) {
			return data.value.data.map(item => ({
				label: toCapitalize(item),
				value: item
			}))
		}
		return data
	}
	async function getSectorsApi() {
		const { toCapitalize } = StringMethods()
		const { data } = await useAPI<IRequestResponse<string[]>>('/resources/sectors')
		let result: SelectOptionContext = []
		if (data.value?.data) {
			result = data.value.data.map(item => ({
				value: item.toLowerCase(),
				label: toCapitalize(item)
			}))
			const { setBusinessSectors } = useUser()
			setBusinessSectors(result)
		}
		return result
	}

	const ReturnList = (arg: string[] | undefined) => {
		if (Array.isArray(arg)) {
			return arg.map(item => ({
				label: toCapitalize(item),
				value: item
			}))
		}
		return []
	}
	const getStatesWithLGA = async () => {
		const { data } = await useAPI<IRequestResponse<{ states: LocalLgaStateContext[] }>>('/states-with-lga')
		if (Array.isArray(data.value?.data.states)) {
			const { setLocalLgaState } = useUser()
			const result =
				data.value?.data.states.map(item => ({
					lga: item.lgas?.map(item => ({
						label: toCapitalize(item),
						value: item?.toLowerCase() || ''
					})),
					label: toCapitalize(item.state ?? ''),
					value: (item.state as string) || ''
				})) ?? []
			setLocalLgaState(result)
			return result
		}
		return data
	}

	const AiicoStates = async () => {
		const { data } = await useAPI<IRequestResponse<string[]>>('/comprehensive-auto-states')
		return ReturnList(data.value?.data)
	}

	const AlianzStates = async () => {
		const { data } = await useAPI<IRequestResponse<string[]>>('/get-allianz-states')
		return ReturnList(data.value?.data)
	}

	const downloadBatchTemplate = async (product_id: string) => {
		const { data } = await useAPI<Blob>(`/batch-purchase/download-policy-template/${product_id}`)
		return data.value
	}
	const colorListApi = async () => {
		const { data } = await useAPI<IRequestResponse<string[]>>('/color-list')
		if (Array.isArray(data.value?.data)) {
			const { setColors } = useStore()
			const colorsList =
				data.value?.data.map(item => ({
					label: toCapitalize(item),
					value: item
				})) ?? []
			setColors(colorsList)
			return colorsList
		}
		return []
	}
	const uploadFileApi = async (payload: FormData) => {
		const config = useRuntimeConfig()
		const response = await fetch(config.public.APIUrlBase + '/upload-file', {
			body: payload,
			method: 'POST',
			headers: {
				Authorization: `Bearer ${cookie.value}`
			}
		}).then(res => res.json())
		return toRef<IRequestResponse<{ file_url: string }>>(response)
	}
	const documentTypesApi = async () => {
		const { data } = await useAPI<IRequestResponse<string[]>>('/verification-types')
		if (Array.isArray(data.value?.data)) {
			const { setDocumentTypes } = useStore()
			const documentTypes =
				data.value?.data.map(item => ({
					label: toCapitalize(item),
					value: item
				})) ?? []
			setDocumentTypes(documentTypes)
			return documentTypes
		}
		return []
	}
	const batchUploadTemplate = async (plan_id: string) => {
		return await useAPI<Blob>(`batch/download-employee-template/${plan_id}`)
	}
	const relationshipsList = async (query?: object) => {
		const { data } = await useAPI<IRequestResponse<string[]>>('/resources/relationships', {
			query
		})
		if (data.value?.data) {
			return data.value.data.map(item => ({
				value: item,
				label: toCapitalize(item)
			}))
		}
		return []
	}
	/**
	 * @param { query } query - The query parameters for the hospital list.
	 * @example  { location: string;   address?: string;  name?: string; search?: string;  }
	 * */
	const getHospitalList = async (query?: Record<string, unknown>) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<HospitalListSchema[]>>>('/hospitals/list', {
			query
		})
		return data
	}
	const getDiscountDetails = async (promo_code: string) => {
		//
		const { data } = await useAPI<IRequestResponse<HospitalListResponse>>('/campaigns/get-by', {
			query: {
				code: promo_code
			}
		})
		return data
	}
	const markTodoAsComplete = async (todo_id: string) => {
		const { data } = await useAPI<IRequestResponse<unknown>>(`/todos/${todo_id}/completed`, {
			method: 'PUT'
		})
		return data
	}
	const getAmountHistories = async (policy_id: string) => {
		const { data } = await useAPI<IRequestResponse<AmountUsedHistory>>('/activities/health-benefits-usage', {
			query: {
				policy_id
			}
		})
		return data.value
	}

	const validatePlateNumber = async (query: { plate_number: string }) => {
		const { data } = await useAPI<IRequestResponse<{ exists: boolean }>>('auto/validate-plate-number', { query })
		return data.value
	}
	const validateSerialNumber = async (query: { serial_number: string }) => {
		const { data } = await useAPI<IRequestResponse<{ exists: boolean }>>('gadget/validate-serial-number', { query })
		return data.value
	}
	const generateClaimInspectionLinks = async (policy_id: string) => {
		const { data } = await useAPI<IRequestResponse<{}>>('policies/generate-inspection-and-claims-link', {
			query: { policy_id }
		})
		return data.value
	}

	const processAutoMedia = async (payload: { img_url: string; document_type: string }) => {
		const { data } = await useAPI<IRequestResponse<ApiMediaType>>('/read-image', { query: payload })
		return data.value
	}

	const validateCACNumber = async (payload: { rcNumber: string | number; name?: string }) => {
		const { data } = await useAPI<IRequestResponse<CACResponse>>('/verifications/prospects/cac', {
			body: payload,
			method: 'POST'
		})
		return data.value
	}
	const validateCustomerCACNumber = async (no: string) => {
		const { data } = await useAPI<IRequestResponse<UserAccountContext>>('/verifications/customers/cac', {
			body: { rcNumber: no },
			method: 'POST'
		})
		return data.value
	}

	const validateNINNumber = async (
		no: string,
		payload?: { first_name: string; last_name: string; email: string }
	) => {
		const { data } = await useAPI<IRequestResponse<NINResponse>>('/verifications/customers/nin', {
			body: { nin: no, ...(payload || {}) },
			method: 'POST'
		})
		return data.value
	}
	const getCompanyInfo = async () => {
		const { data } = await useAPI<IRequestResponse<CompanySupport[]>>('/company_info', {
			baseURL: import.meta.env.VITE_LANDING_PAGE_API
		})
		return data.value
	}

	/**
	 * @description This api returns the list of hospital visits
	 * @param {object} query - The query to be used to fetch the list of hospital visits
	 * @example getHospitalVisits({ plan_id: '1', policy_id: '1'})
	 * */
	const getHospitalVisits = async (query?: Record<string, unknown>) => {
		const { data } = await useAPI<IRequestResponse<HospitalVisitResponse[]>>('/visits', {
			query
		})
		return data.value
	}

	return {
		getHospitalVisits,
		getCompanyInfo,
		validateCustomerCACNumber,
		validateNINNumber,
		validateCACNumber,
		getSectorsApi,
		getStatesWithLGA,
		AiicoStates,
		getCoverPeriod,
		GetTitles,
		GetAiicoTitles,
		downloadBatchTemplate,
		colorListApi,
		uploadFileApi,
		documentTypesApi,
		batchUploadTemplate,
		relationshipsList,
		getHospitalList,
		getDiscountDetails,
		AlianzStates,
		markTodoAsComplete,
		getAmountHistories,
		validatePlateNumber,
		validateSerialNumber,
		generateClaimInspectionLinks,
		processAutoMedia,
		getVehicleTypes: async () => {
			const { data } = await useAPI<IRequestResponse<VehicleMakeResponse>>('/vehicles/vehicle-types', {})

			if (data.value?.data) {
				return data.value.data.map(item => ({
					label: item.name,
					value: item.id,
					id: item.id
				}))
			}

			return []
		},
		getVehicleMake: async () => {
			const { data } = await useAPI<IRequestResponse<VehicleMakeResponse>>('/vehicles/vehicle-makes', {})
			if (data.value?.data) {
				const { setVehicleMake } = useProducts()
				const result = data.value.data.map(item => ({
					label: item.name,
					value: item.id,
					id: item.id
				}))
				setVehicleMake(result)
				return result
			}
			return []
		},
		getVehicleModels: async (vehicle_make_id: number | string) => {
			const { data } = await useAPI<IRequestResponse<VehicleMakeResponse>>(
				`/vehicles/vehicle-models/${vehicle_make_id || ''}`
			)
			if (data.value?.data) {
				const { toCapitalize } = StringMethods()
				return data.value.data.map(item => ({
					label: toCapitalize(item.name),
					value: item.id,
					id: item.id
				}))
			}
			return []
		},
		getGadgetCategories: async (product_id: string) => {
			const { data } = await useAPI<IRequestResponse<PaginationContext<GadgetCategoriesContext[]>>>(
				`/gadget/categories/${product_id}`,
				{}
			)
			if (data.value?.data && Array.isArray(data.value.data.items)) {
				return data.value.data.items.map(item => ({
					...item,
					label: item.name,
					value: item.id
				})) as SelectOptionContext
			}
			return []
		},
		getGadgetCategory: async (payload: string) => {
			const { data } = await useAPI<IRequestResponse<GadgetCategoriesContext>>(`/gadget/categories/${payload}`)
			return data.value
		},
		getCategoryBrands: async (category_id: string) => {
			const { data } = await useAPI<IRequestResponse<GadgetBrandResponse[]>>(
				`/gadget/categories/${category_id}/brands`
			)
			return data.value
		},
		getGadgetBrands: async () => {
			const { data } = await useAPI<IRequestResponse<PaginationContext<GadgetBrandResponse>>>(
				'/gadget/brands',
				{}
			)
			return data.value
		},
		getTravelLocations: async () => {
			const { data } = await useAPI<IRequestResponse<TravelLocationResponse>>('travel/locations', {})
			return data.value
		}
	}
}
